/**
 * @module components/layout/AnalysesRemainingBadge
 * @description Analyses-remaining pill with a progress meter driven by
 * Option B account primaryTier (plan / free allotment / paid high-water).
 *
 * @see docs/ANALYSES_METER_IMPLEMENTATION_PLAN.md
 */

import React from 'react';

/**
 * @param {Object} props
 * @param {number} props.totalAnalysesRemaining - Analyses left for this account.
 * @param {number} props.analysesPercent - Meter fill percentage (0-100).
 * @param {number} props.analysesMeterMax - Meter max for aria-valuemax.
 * @param {() => void} props.onPurchaseClick - Called when the "Purchase analyses" link is clicked (0-remaining state).
 * @param {'desktop'|'mobile'} [props.variant='desktop'] - Layout variant; mobile is centered with larger touch targets.
 */
const AnalysesRemainingBadge = ({
  totalAnalysesRemaining,
  analysesPercent,
  analysesMeterMax,
  onPurchaseClick,
  variant = 'desktop',
}) => {
  const isMobile = variant === 'mobile';

  return (
    <div
      className={
        isMobile
          ? 'flex flex-col gap-2 bg-gradient-to-r from-apple-blue/20 to-apple-purple/20 backdrop-blur-xl px-6 py-3 rounded-xl border border-apple-blue/30 min-w-[240px]'
          : 'flex flex-col gap-2 bg-gradient-to-r from-apple-blue/20 to-apple-purple/20 backdrop-blur-xl px-4 py-2.5 rounded-2xl border border-apple-blue/30 min-w-[220px]'
      }
    >
      <div className="flex items-center gap-2.5 w-full">
        <div
          className={
            isMobile
              ? 'w-3 h-3 bg-apple-blue rounded-full animate-pulse shrink-0'
              : 'w-2 h-2 bg-apple-blue rounded-full animate-pulse shrink-0'
          }
          aria-hidden
        />
        <div className={isMobile ? 'flex-1 min-w-0 text-center' : 'flex-1 min-w-0 text-left'}>
          {totalAnalysesRemaining === 0 ? (
            <button
              type="button"
              onClick={() => onPurchaseClick && onPurchaseClick()}
              className={
                isMobile
                  ? 'text-sm text-critic-text-primary font-semibold underline underline-offset-2 decoration-white/30 hover:decoration-white/70'
                  : 'text-sm text-white font-semibold underline underline-offset-2 decoration-white/50 hover:decoration-white'
              }
            >
              Purchase analyses
            </button>
          ) : (
            <span
              className={
                isMobile
                  ? 'text-sm text-critic-text-primary font-semibold tabular-nums'
                  : 'text-sm text-white font-semibold tabular-nums'
              }
            >
              {totalAnalysesRemaining}{' '}
              {totalAnalysesRemaining === 1 ? 'analysis' : 'analyses'}
            </span>
          )}
        </div>
      </div>
      {totalAnalysesRemaining > 0 && (
        <div
          className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden"
          role="meter"
          aria-valuemin={0}
          aria-valuenow={totalAnalysesRemaining}
          aria-valuemax={Math.max(analysesMeterMax, 1)}
          aria-label="Analyses remaining"
        >
          <div
            className="h-full rounded-full bg-gradient-insight transition-[width] duration-300 ease-out"
            style={{ width: `${analysesPercent}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default AnalysesRemainingBadge;
